import { useCallback, useEffect, useState } from "react";

import { Avatar } from "@excalidraw/excalidraw/components/Avatar";
import { Button } from "@excalidraw/excalidraw/components/Button";
import { Island } from "@excalidraw/excalidraw/components/Island";
import { ExcalLogo } from "@excalidraw/excalidraw/components/icons";
import Spinner from "@excalidraw/excalidraw/components/Spinner";

interface AuthUser {
  sub: string;
  email: string;
  name: string;
  picture?: string;
}

interface LoginContext {
  allowedDomains: string[];
}

interface ConsentContext {
  clientName: string;
  redirectUri: string;
  scopes: Array<{ name: string; description: string }>;
  user: AuthUser;
}

const LOGIN_ERRORS: Record<string, string> = {
  state: "The sign-in attempt expired. Try again.",
  denied: "Google sign-in was cancelled.",
  not_allowed: "That Google account is not allowed on this canvas.",
  code: "Google did not return an authorization code.",
  exchange: "Google sign-in could not be completed. Try again.",
};

function useDarkTheme() {
  const [dark, setDark] = useState(
    () => document.documentElement.dataset.authTheme === "dark",
  );

  useEffect(() => {
    const query = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => {
      if (!localStorage.getItem("excalidraw-theme")) {
        setDark(query.matches);
      }
    };
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, []);

  return dark;
}

const GoogleMark = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
    <path
      fill="#4285F4"
      d="M46.98 24.55c0-1.57-.14-3.09-.4-4.55H24v8.61h12.94c-.56 3.02-2.26 5.58-4.82 7.29v6.06h7.81c4.57-4.2 7.05-10.39 7.05-17.41z"
    />
    <path
      fill="#34A853"
      d="M24 48c6.48 0 11.93-2.15 15.9-5.84l-7.81-6.06c-2.16 1.45-4.92 2.3-8.09 2.3-6.22 0-11.49-4.2-13.37-9.85H2.55v6.26C6.5 42.6 14.55 48 24 48z"
    />
    <path
      fill="#FBBC05"
      d="M10.63 28.55A14.4 14.4 0 0 1 9.88 24c0-1.58.27-3.12.75-4.55v-6.26H2.55A24 24 0 0 0 0 24c0 3.87.93 7.54 2.55 10.81l8.08-6.26z"
    />
    <path
      fill="#EA4335"
      d="M24 9.6c3.52 0 6.68 1.21 9.17 3.59l6.88-6.88C35.92 2.38 30.47 0 24 0 14.55 0 6.5 5.4 2.55 13.19l8.08 6.26C12.51 13.8 17.78 9.6 24 9.6z"
    />
  </svg>
);

const Frame = ({
  children,
  dark,
}: {
  children: React.ReactNode;
  dark: boolean;
}) => (
  <div className={`excalidraw sa-auth ${dark ? "theme--dark" : ""}`}>
    <div className="sa-auth__inner">
      <div className="sa-auth__brand">
        <span className="sa-auth__mark">{ExcalLogo}</span>
        <span className="sa-auth__wordmark">Superaligned Draw</span>
      </div>
      <Island className="sa-auth__card" padding={10}>
        {children}
      </Island>
      <p className="sa-auth__footnote">
        Self-hosted Excalidraw, shared with agents over MCP.
      </p>
    </div>
  </div>
);

const Login = ({ dark }: { dark: boolean }) => {
  const params = new URLSearchParams(window.location.search);
  const error = params.get("error");
  const returnTo = params.get("return") || "/";
  const [context, setContext] = useState<LoginContext | null>(null);

  useEffect(() => {
    fetch("/api/auth/config", { credentials: "same-origin" })
      .then((response) => (response.ok ? response.json() : null))
      .then((value) => setContext(value))
      .catch(() => setContext(null));
  }, []);

  const domains = context?.allowedDomains ?? [];

  return (
    <Frame dark={dark}>
      <h1 className="sa-auth__title">Sign in</h1>
      <p className="sa-auth__lede">
        Continue with your Superaligned Google account to open the canvas.
      </p>

      {error && (
        <div className="sa-auth__error" role="alert">
          {LOGIN_ERRORS[error] ?? "Sign-in failed. Try again."}
        </div>
      )}

      <Button
        className="sa-auth__google"
        onSelect={() => {
          window.location.assign(
            `/oauth/google/start?return=${encodeURIComponent(returnTo)}`,
          );
        }}
      >
        <GoogleMark />
        <span>Continue with Google</span>
      </Button>

      <p className="sa-auth__hint">
        {domains.length
          ? `Only ${domains.map((domain) => `@${domain}`).join(" and ")} accounts can sign in.`
          : "Only Superaligned Google accounts can sign in."}
      </p>
    </Frame>
  );
};

const Consent = ({ dark }: { dark: boolean }) => {
  const requestId = new URLSearchParams(window.location.search).get("request");
  const [context, setContext] = useState<ConsentContext | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!requestId) {
      setError("This authorization request is missing its identifier.");
      return;
    }
    fetch(`/api/auth/consent/${encodeURIComponent(requestId)}`, {
      credentials: "same-origin",
    })
      .then(async (response) => {
        if (response.status === 401) {
          window.location.assign(
            `/login?return=${encodeURIComponent(window.location.pathname + window.location.search)}`,
          );
          return null;
        }
        if (!response.ok) {
          throw new Error("request_expired");
        }
        return response.json();
      })
      .then((value) => value && setContext(value))
      .catch(() => setError("This authorization request expired. Start again from your MCP client."));
  }, [requestId]);

  const decide = useCallback(
    async (decision: "allow" | "deny") => {
      if (!requestId) {
        return;
      }
      setSubmitting(true);
      try {
        const response = await fetch(
          `/api/auth/consent/${encodeURIComponent(requestId)}`,
          {
            method: "POST",
            credentials: "same-origin",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ decision }),
          },
        );
        const payload = await response.json();
        if (!response.ok || !payload.redirectTo) {
          throw new Error(payload.error ?? "failed");
        }
        window.location.assign(payload.redirectTo);
      } catch {
        setSubmitting(false);
        setError("The request could not be completed. Start again from your MCP client.");
      }
    },
    [requestId],
  );

  if (error) {
    return (
      <Frame dark={dark}>
        <h1 className="sa-auth__title">Authorization</h1>
        <p className="sa-auth__lede">{error}</p>
        <Button
          className="sa-auth__secondary"
          onSelect={() => window.location.assign("/")}
        >
          Go to Draw
        </Button>
      </Frame>
    );
  }

  if (!context) {
    return (
      <Frame dark={dark}>
        <div className="sa-auth__loading">
          <Spinner size="1.5rem" />
          <span>Checking the request</span>
        </div>
      </Frame>
    );
  }

  const target = (() => {
    try {
      return new URL(context.redirectUri).host;
    } catch {
      return context.redirectUri;
    }
  })();

  return (
    <Frame dark={dark}>
      <div className="sa-auth__account">
        <Avatar
          color="#7263d8"
          name={context.user.name}
          src={context.user.picture}
          onClick={() => undefined}
        />
        <div>
          <div className="sa-auth__account-name">{context.user.name}</div>
          <div className="sa-auth__account-email">{context.user.email}</div>
        </div>
      </div>

      <h1 className="sa-auth__title">Authorize {context.clientName}</h1>
      <p className="sa-auth__lede">
        <strong>{context.clientName}</strong> wants to use the Draw MCP server
        and will send you back to <span className="sa-auth__mono">{target}</span>.
      </p>

      <ul className="sa-auth__scopes">
        {context.scopes.map((scope) => (
          <li key={scope.name}>
            <span className="sa-auth__scope-name">{scope.name}</span>
            <span className="sa-auth__scope-description">
              {scope.description}
            </span>
          </li>
        ))}
      </ul>

      <div className="sa-auth__actions">
        <Button
          className="sa-auth__secondary"
          onSelect={() => decide("deny")}
          disabled={submitting}
        >
          Deny
        </Button>
        <Button
          className="sa-auth__primary"
          onSelect={() => decide("allow")}
          disabled={submitting}
        >
          {submitting ? "Authorizing" : "Allow"}
        </Button>
      </div>
    </Frame>
  );
};

export const AuthApp = () => {
  const dark = useDarkTheme();
  const path = window.location.pathname;

  if (path === "/oauth/consent") {
    return <Consent dark={dark} />;
  }

  return <Login dark={dark} />;
};
